(() => {
  if (!customElements.get('editorial-collection')) {
    customElements.define('editorial-collection', class extends HTMLElement {
      connectedCallback() {
        this.features = window.PolewardEditorialFeatures?.connectOwner(this);
        this.onClick = (event) => {
          const button = event.target.closest('[data-epg-columns]');
          if (!button || !this.contains(button)) return;
          const columns = Number(button.dataset.epgColumns);
          if (![2, 4, 8].includes(columns)) return;
          this.style.setProperty('--epg-desktop-columns', columns);
          this.querySelectorAll('[data-epg-columns]').forEach((item) => {
            item.setAttribute('aria-pressed', String(Number(item.dataset.epgColumns) === columns));
          });
          this.dispatchEvent(new CustomEvent('epg:layout'));
        };
        this.addEventListener('click', this.onClick);
        this.setAttribute('data-ready', '');
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.features?.destroy();
      }
    });
  }

  if (!customElements.get('editorial-product-grid')) {
    customElements.define('editorial-product-grid', class extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('.epg-grid');
        this.owner = this.closest('editorial-collection');
        if (!this.grid || !this.owner) return;
        this.products = [...this.grid.querySelectorAll(':scope > [data-epg-product]')];
        this.inserts = [...this.grid.querySelectorAll(':scope > [data-epg-insert]')];
        this.frame = null;
        this.lastLayout = '';
        this.queueLayout = () => {
          if (this.frame !== null) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.layout();
          });
        };
        this.owner.addEventListener('epg:layout', this.queueLayout);
        this.observer = new ResizeObserver(this.queueLayout);
        this.observer.observe(this.grid);
        this.onBlockSelect = (event) => {
          const insert = this.inserts.find((item) => item.dataset.blockId === event.detail.blockId);
          if (insert && !insert.hidden) insert.scrollIntoView({ block: 'center', behavior: 'auto' });
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.queueLayout();
        this.features = window.PolewardEditorialFeatures?.connectGrid(this);
      }

      layout() {
        const columns = Number.parseInt(getComputedStyle(this.owner).getPropertyValue('--epg-columns'), 10) || 2;
        const mobile = window.matchMedia('(max-width: 989px)').matches;
        const key = `${columns}:${mobile}:${this.grid.clientWidth}`;
        if (this.lastLayout === key) return;
        this.lastLayout = key;
        const entries = this.inserts.map((insert) => ({
          insert,
          after: insert.dataset.placement === 'products'
            ? Number(insert.dataset.afterProducts)
            : Number(insert.dataset.afterRows) * columns,
          sequence: Number(insert.dataset.blockPosition),
        })).sort((a, b) => a.after - b.after || a.sequence - b.sequence);

        entries.forEach(({ insert, after }) => {
          const show = Number.isFinite(after) && after >= 0 && after <= this.products.length;
          insert.hidden = !show;
          if (!show) return;
          const spanSetting = mobile ? insert.dataset.mobileSpan : insert.dataset.span;
          const span = spanSetting === 'full' ? columns : Math.min(Number(spanSetting) || 1, columns);
          insert.style.gridColumn = span === columns ? '1 / -1' : `span ${span}`;
          insert.dataset.fullRow = String(span === columns);
          this.grid.insertBefore(insert, this.products[after] || null);
        });

        const firstCard = this.products[0]?.querySelector('.epg-card__media');
        if (firstCard) {
          const sizes = `${Math.ceil(firstCard.getBoundingClientRect().width)}px`;
          this.products.forEach((product) => {
            product.querySelectorAll('.epg-card__image').forEach((image) => { image.sizes = sizes; });
          });
        }
      }

      disconnectedCallback() {
        this.features?.destroy();
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.observer?.disconnect();
        this.owner?.removeEventListener('epg:layout', this.queueLayout);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
    });
  }

  if (!customElements.get('editorial-product-card')) {
    customElements.define('editorial-product-card', class extends HTMLElement {
      connectedCallback() {
        this.images = [...this.querySelectorAll('.epg-card__image')];
        if (this.images.length < 2) return;
        this.onEnter = () => {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.showImage(1);
        };
        this.onLeave = () => this.showImage(0);
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.showImage(0);
        this.setAttribute('data-ready', '');
      }

      showImage(index) {
        this.dataset.imageIndex = index;
        this.images.forEach((image, i) => image.setAttribute('aria-hidden', String(i !== index)));
      }

      disconnectedCallback() {
        this.removeEventListener('mouseenter', this.onEnter);
        this.removeEventListener('mouseleave', this.onLeave);
      }
    });
  }
})();
